import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useHasCompletedTransaction } from "@/lib/use-completed-transaction";
import { submitAppFeedback } from "@/lib/app-feedback";
import { StarRating } from "@/components/StarRating";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

export function FeedbackPrompt() {
  const { user } = useAuth();
  const { eligible } = useHasCompletedTransaction(user?.id);
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [pros, setPros] = useState("");
  const [cons, setCons] = useState("");
  const [general, setGeneral] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Only ask people who have actually completed a swap, lend, or donation.
    if (!user || !eligible) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("feedback_submitted_at,last_feedback_prompt_at")
        .eq("id", user.id)
        .maybeSingle();
      if (cancelled || !data) return;
      if (data.feedback_submitted_at) return; // already gave feedback — stop forever
      const last = data.last_feedback_prompt_at ? new Date(data.last_feedback_prompt_at).getTime() : 0;
      if (Date.now() - last >= TWO_WEEKS_MS) {
        setTimeout(() => setOpen(true), 1500);
      }
    })();
    return () => { cancelled = true; };
  }, [user, eligible]);

  async function dismiss() {
    setOpen(false);
    if (!user) return;
    await supabase.from("profiles").update({ last_feedback_prompt_at: new Date().toISOString() }).eq("id", user.id);
  }

  async function submit() {
    if (!user) return;
    if (!rating && !pros.trim() && !cons.trim() && !general.trim()) {
      return toast.error("Please give a rating or share a comment.");
    }
    setSaving(true);
    const res = await submitAppFeedback(user.id, {
      rating: rating || null,
      pros: pros.trim() || null,
      cons: cons.trim() || null,
      general: general.trim() || null,
    });
    setSaving(false);
    if (!res.ok) return toast.error("Could not submit. Try again.");
    setOpen(false);
    toast.success("Thank you for your feedback! 💜");
  }

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>How is Book Swap working for you? 📚</DialogTitle>
          <DialogDescription>You've completed an exchange — a quick rating helps us improve. We'll only ask once if you submit.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Overall rating</Label>
            <div className="mt-1"><StarRating value={rating} onChange={setRating} /></div>
          </div>
          <div>
            <Label htmlFor="pros">What did you like?</Label>
            <Textarea id="pros" maxLength={1000} value={pros} onChange={(e) => setPros(e.target.value)} placeholder="The parts that worked well…" />
          </div>
          <div>
            <Label htmlFor="cons">What could be improved?</Label>
            <Textarea id="cons" maxLength={1000} value={cons} onChange={(e) => setCons(e.target.value)} placeholder="Anything confusing or missing…" />
          </div>
          <div>
            <Label htmlFor="general">Anything else?</Label>
            <Textarea id="general" maxLength={1000} value={general} onChange={(e) => setGeneral(e.target.value)} placeholder="Optional" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={dismiss}>Maybe later</Button>
          <Button onClick={submit} disabled={saving} className="bg-primary hover:bg-primary/90">
            {saving ? "Submitting…" : "Send feedback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
